import { useSendCourseReminder } from '@/hooks/useSendCourseReminder';
import { useLocale } from '@/providers/LocaleProvider';
import InfoIcon from '@/resources/images/icons-info-circle.svg';
import { Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { Alert } from '../Alert';
import { TextField } from '../TextField';
import { Dialog } from './Dialog';

interface SendReminderDialogProps {
  open: boolean;
  /** Employee receiving the reminder. */
  employeeId?: string;
  /** Name shown in the dialog body for context. */
  studentName?: string;
  courseId?: string;
  courseName?: string;
  theme?: any;
  onClose: () => void;
  onSuccess?: () => void;
}

export const SendReminderDialog = ({
  open,
  employeeId,
  studentName,
  courseId,
  courseName,
  theme,
  onClose,
  onSuccess,
}: SendReminderDialogProps) => {
  const { translate } = useLocale();
  const [message, setMessage] = useState('');

  const { mutate: sendReminder, isPending } = useSendCourseReminder({
    onSuccess: () => {
      handleClose();
      onSuccess?.();
    },
  });

  const handleClose = () => {
    setMessage('');
    onClose();
  };

  const handleSend = () => {
    if (!employeeId || !courseId) return;
    const trimmed = message.trim();
    sendReminder({
      employeeId,
      courseId,
      message: trimmed.length ? trimmed : undefined,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      theme={theme}
      maxWidth='sm'
      title={translate('manage.courseStudents.sendReminderTitle')}
      actions={[
        {
          label: translate('common.send'),
          variant: 'filled',
          onClick: handleSend,
          disabled: isPending || !employeeId || !courseId,
          isLoading: isPending,
        },
        {
          label: translate('common.cancel'),
          onClick: handleClose,
          disabled: isPending,
        },
      ]}
    >
      <Stack gap={3}>
        <Alert severity='info' icon={<InfoIcon />}>
          <Typography
            variant='body2'
            dangerouslySetInnerHTML={{
              __html: translate('manage.courseStudents.sendReminderDescription', {
                studentName: studentName ?? '',
                courseName: courseName ?? '',
              }),
            }}
          />
        </Alert>

        <TextField
          label={translate('manage.courseStudents.reminderMessageLabel')}
          placeholder={translate('manage.courseStudents.reminderMessagePlaceholder')}
          value={message}
          onChange={(e) => setMessage(e.target.value as string)}
          multiline
          minRows={4}
          maxRows={8}
          fullWidth
          clearable={false}
          autoComplete={false}
          disabled={isPending}
        />
      </Stack>
    </Dialog>
  );
};
